import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FaIcon, faFileInvoice } from '@/lib/fa'
import { Card } from '@/app/features/common/Card'
import { PRINTOUT_ROUTES } from '@/modules/printouts/routes-typed'

import usePrintOutService from '@/modules/printouts/services/PrintOutService'
import {
  getReportTypeByNumber,
  getReportNameByType,
} from '@/app/services/printout/reportTypeResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ReportType } from '@/app/constants/reportType'
import { EtBrandSygnet } from '@/modules/shared/components/svg/EtBrandSygnet'

type Props = {
  reports?: { id: string; name: string }[]
  className?: string
}

/**
 * Domyślne ulubione raporty pokazywane, gdy użytkownik nie wybrał żadnego
 * własnego. Cztery raporty z grupy kontroli poprawności danych + Naruszenia
 * jako najczęściej używany raport w programie.
 */
const DEFAULT_FAVORITE_REPORT_TYPES: ReportType[] = [
  ReportType.DRIVER_DATA_COMPLETNESS,
  ReportType.DRIVER_ATTESTATION_REPORT,
  ReportType.CARD_READING_DEADLINE,
  ReportType.DRIVER_CARD_READING,
  ReportType.VIOLATIONS,
]

export const FavouriteReportsCard = ({ className }: Props) => {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const { getFavPrintOuts } = usePrintOutService()
  const { t } = useLocalize()
  const [favoriteReports, setFavoriteReports] = React.useState<
    Array<{ id: string; name: string; type: ReportType }>
  >([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {

    if (!companyId) {
      setLoading(false)
      return
    }

    let cancelled = false
    const loadFavorites = async () => {
      try {
        const favReports = await getFavPrintOuts({ companyId })
        if (cancelled) return
        if (Array.isArray(favReports)) {
          const reports = favReports
            .map((item: { reportType: number; id: string; name: string }) => {
              const reportType = getReportTypeByNumber(item.reportType)
              if (reportType !== ReportType.NONE) {
                const translationKey = getReportNameByType(reportType)
                return {
                  id: item.id,
                  name: t(translationKey) || item.name,
                  type: reportType,
                }
              }
              return null
            })
            .filter(Boolean) as Array<{
            id: string
            name: string
            type: ReportType
          }>
          setFavoriteReports(reports)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load favorite printouts:', error)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadFavorites()
    return () => {
      cancelled = true
    }
  }, [companyId])

  const handleOpenAll = () => {
    const path = PRINTOUT_ROUTES.favoritePrintouts.buildPath({
      companyId: companyId,
    })
    navigate(path)
  }

  if (loading) {
    return (
      <Card
        className={className}
        title={t('dashboard.favourite-printouts')}
        action={handleOpenAll}
        classNameContent="!tw-overflow-y-auto !tw-p-4"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
          <p className="tw-text-sm tw-text-content-secondary">{t('common.loading')}</p>
        </div>
      </Card>
    )
  }

  const reportsToShow =
    favoriteReports.length > 0
      ? favoriteReports
      : DEFAULT_FAVORITE_REPORT_TYPES.map((type) => ({
          id: `default-${type}`,
          name: t(getReportNameByType(type)),
          type,
        }))

  return (
    <Card
      className={className}
      title={t('dashboard.favourite-printouts')}
      action={handleOpenAll}
      classNameContent="!tw-overflow-y-auto !tw-p-4 tw-min-h-full"
    >
      <div className="tw-flex tw-flex-col">
        {reportsToShow.map((report) => (
          <div
            key={report.id}
            className="tw-flex tw-min-w-min tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-base-300 tw-py-3 tw-text-sm first:tw-pt-0 last:tw-border-none last:tw-pb-0"
          >
            <div className="tw-flex tw-items-center tw-gap-1">
              <FaIcon icon={faFileInvoice} className="tw-h-[10px] tw-w-3" />
              {report.name}
            </div>
            <span>
              <EtBrandSygnet className="tw-h-4 tw-w-4 tw-text-warning" />
            </span>
          </div>
        ))}
      </div>
    </Card>
  )
}
